<script setup>
/**
 * FormControl Factory
 * Dynamically resolves and renders form components based on table names,
 * column names, or field types.
 * * Note: Uses shallowRef to avoid Proxy-wrapping component definitions.
 */
import {computed, onMounted, shallowRef} from 'vue'
import {useRoute} from 'vue-router'
import camelCase from 'lodash/camelCase'
import {useComponentsStore} from '@/stores/components'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'

// --- Imports (Maintaining your existing list) ---
import FormControlCoursesDatesBookings from "@/bespoke/flyspain/FormControlCoursesDatesBookings"
import FormControlMRegistrantsRegistrantsCreditAmount
    from "@/bespoke/odp/FormControlMRegistrantsRegistrantsCreditAmount"
import FormControlOdpCourseBookingsAmountPaid from "@/bespoke/odp/FormControlOdpCourseBookingsAmountPaid"
import FormControlOdpCourseBookingsCourseDateId from "@/bespoke/odp/FormControlOdpCourseBookingsCourseDateId"
import FormControlOdpCourseDatesPromotionId from "@/bespoke/odp/FormControlOdpCourseDatesPromotionId"
import FormControlOdpCourseDatesCourseId from "@/bespoke/odp/FormControlOdpCourseDatesCourseId"
import FormControlOdpCourseDatesBookings from "@/bespoke/odp/FormControlOdpCourseDatesBookings"
import FormControlOdpCourseDatesEvent from "@/bespoke/odp/FormControlOdpCourseDatesEvent"
import FormControlOdpPartyBookingsResendEmail from "@/bespoke/odp/FormControlOdpPartyBookingsResendEmail"
import FormControlCategoriesComponentMenu from "@/common/form-control/FormControlCategoriesComponentMenu.vue"
import FormControlActivityRowId from "@/common/form-control/FormControlActivityRowId.vue"
import FormControlUsersAdministrator from "@/common/form-control/FormControlUsersAdministrator"
import FormControlUsersAllowCacheClearance from "@/common/form-control/FormControlUsersAllowCacheClearance"
import FormControlUsersRoles from "@/common/form-control/FormControlUsersRoles"
import FormControlUsersGroupsRoles from "@/common/form-control/user-groups-roles/FormControlUsersGroupsRoles"
import FormControlProductVariablesOptionId from "@/common/form-control/FormControlProductVariablesOptionId"
import FormControlProductTypesButtons from "@/common/form-control/FormControlProductTypesButtons"
import FormControlProductTypesFeatures from "@/common/form-control/FormControlProductTypesFeatures"
import FormControlProductTypesShipping from "@/common/form-control/FormControlProductTypesShipping"
import FormControlProductOptionsButtons from "@/common/form-control/FormControlProductOptionsButtons"
import FormControlProductOptionsProductTypeId from "@/common/form-control/FormControlProductOptionsProductTypeId"
import FormControlMProductsProductsTypeId from "@/common/form-control/FormControlMProductsProductsTypeId"
import FormControlMProductsProductsCategoryId from "@/common/form-control/FormControlMProductsProductsCategoryId"
import FormControlMProductsProductsTypeFeatures from "@/common/form-control/FormControlMProductsProductsTypeFeatures"
import FormControlPagesComponentId from "@/common/form-control/FormControlPagesComponentId"
import FormControlPagesDisplayInSitemap from "@/common/form-control/FormControlPagesDisplayInSitemap"
import FormControlPagesNavigation from "@/common/form-control/FormControlPagesNavigation"
import FormControlPagesTemplateId from "@/common/form-control/FormControlPagesTemplateId"
import FormControlPagesType from "@/common/form-control/FormControlPagesType"
import FormControlPagesUrl from "@/common/form-control/FormControlPagesUrl"
import FormControlPagesUrlSlug from "@/common/form-control/FormControlPagesUrlSlug"
import FormControlMRegistrantsRegistrantsLogin from "@/common/form-control/FormControlMRegistrantsRegistrantsLogin"
import FormControlMRegistrantsRegistrantsSessionLogs
    from "@/common/form-control/FormControlMRegistrantsRegistrantsSessionLogs"
import FormControlMRegistrantsRegistrantsUsername from "@/common/form-control/FormControlMRegistrantsRegistrantsUsername"
import FormControlMBasketMerchantdetailsSettings from "@/common/form-control/FormControlMBasketMerchantdetailsSettings"
import FormControlModulecomponentsDisplayName from "@/common/form-control/FormControlModulecomponentsDisplayName"
import FormControlModulecomponentsNameField from "@/common/form-control/FormControlModulecomponentsNameField.vue"
import FormControlModulecomponentsTitleColumn from "@/common/form-control/FormControlModulecomponentsTitleColumn"
import FormControlModulecomponentsThumbnailColumn from "@/common/form-control/FormControlModulecomponentsThumbnailColumn"
import FormControlModulecomponentsTableName from "@/common/form-control/FormControlModulecomponentsTableName"
import FormControlModulecomponentsCategoryColumn from "@/common/form-control/FormControlModulecomponentsCategoryColumn"
import FormControlModulecomponentsCategoryComponent from "@/common/form-control/FormControlModulecomponentsCategoryComponent"
import FormControlAccountSettingsSecondaryDomains from "@/common/form-control/FormControlAccountSettingsSecondaryDomains"
import FormControlAccountSettingsPrimaryDomain from "@/common/form-control/FormControlAccountSettingsPrimaryDomain"
import FormControlAccountSettingsReorderScripts from "@/common/form-control/FormControlAccountSettingsReorderScripts"
import FormControlContentPageUse from "@/common/form-control/FormControlContentPageUse"
import FormControlContentClass from "@/common/form-control/FormControlContentClass.vue"
import FormControlContentTitle from "@/common/form-control/FormControlContentTitle"
import FormControlContentContentType from "@/common/form-control/FormControlContentContentType"
import FormControlContentContentTargetContentArea from "@/common/form-control/FormControlContentContentTargetContentArea"
import FormControlMBasketOrderitemsVariationId from "@/common/form-control/FormControlMBasketOrderitemsVariationId"
import FormControlMBasketOrdersCustomerEmailMessageId
    from "@/common/form-control/FormControlMBasketOrdersCustomerEmailMessageId"
import FormControlMBasketOrdersInternalEmailMessageId
    from "@/common/form-control/FormControlMBasketOrdersInternalEmailMessageId"
import FormControlMBasketOrdersLogs from "@/common/form-control/FormControlMBasketOrdersLogs"
import FormControlMBasketOrdersRequestAReview from "@/common/form-control/FormControlMBasketOrdersRequestAReview"
import FormControlMBasketOrdersQuantity from "@/common/form-control/FormControlMBasketOrdersQuantity"
import FormControlMBasketOrdersSessionLog from "@/common/form-control/FormControlMBasketOrdersSessionLog"
import FormControlMProductsProductsShipping from "@/common/form-control/FormControlMProductsProductsShipping"
import FormControlModulestructureBrowser from "@/common/form-control/FormControlModulestructureBrowser"
import FormControlModulestructureCategoryComponentId
    from "@/common/form-control/FormControlModulestructureCategoryComponentId"
import FormControlModulestructureColumnName from "@/common/form-control/FormControlModulestructureColumnName"
import FormControlModulestructureDateFormat from "@/common/form-control/FormControlModulestructureDateFormat"
import FormControlModulestructureDefaultValue from "@/common/form-control/FormControlModulestructureDefaultValue"
import FormControlModulestructureEndYear from "@/common/form-control/FormControlModulestructureEndYear"
import FormControlModulestructureStartYear from "@/common/form-control/FormControlModulestructureStartYear"
import FormControlModulestructureNumRows from "@/common/form-control/FormControlModulestructureNumRows"
import FormControlModulestructureOptions from "@/common/form-control/FormControlModulestructureOptions"
import FormControlModulestructurePassword from "@/common/form-control/FormControlModulestructurePassword"
import FormControlModulestructureSelectType from "@/common/form-control/FormControlModulestructureSelectType"
import FormControlModulestructureType from "@/common/form-control/FormControlModulestructureType"
import FormControlModulestructureTableTargetColumn from "@/common/form-control/FormControlModulestructureTableTargetColumn"
import FormControlStockQuantity from "@/common/form-control/FormControlStockQuantity"
import FormControlUrlSlug from "@/common/form-control/FormControlUrlSlug"
import FormControlPortalSitesBuildName from "@/common/form-control/FormControlPortalSitesBuildName"
import FormControlEmailMessageAttachments from "@/common/form-control/FormControlEmailMessageAttachments"
import FormControlEmailMessageEvent from "@/common/form-control/FormControlEmailMessageEvent"
import FormControlEmailMessageEventDate from "@/common/form-control/FormControlEmailMessageEventDate"
import FormControlEmailMessageHtmlBody from "@/common/form-control/FormControlEmailMessageHtmlBody"
import FormControlVariationsOption from "@/common/form-control/FormControlVariationsOption"
import FormControlTagsTagUsage from '@/common/form-control/form-control-tags-usage/FormControlTagsTagUsage'
import FormControlThemesCssGroups from '@/common/form-control/FormControlThemesCssGroups'
import FormControlTemplatesTemplatesComponentId from '@/common/form-control/FormControlTemplatesTemplatesComponentId'
import FormControlTemplatesTemplatesContentType from '@/common/form-control/FormControlTemplatesTemplatesContentType'
import FormControlTemplatesTemplatesCssGroupName from '@/common/form-control/FormControlTemplatesTemplatesCssGroupName'
import FormControlTemplatesTemplatesFilterAttribute from '@/common/form-control/FormControlTemplatesTemplatesFilterAttribute'
import FormControlTemplatesTemplatesLazyLoad from '@/common/form-control/FormControlTemplatesTemplatesLazyLoad'
import FormControlFilesFileName from '@/common/form-control/FormControlFilesFileName'
import FormControlFilesFileSize from '@/common/form-control/FormControlFilesFileSize'
import FormControlFilesUsage from '@/common/form-control/FormControlFilesUsage'
import FormControlFranchiseId from '@/common/form-control/FormControlFranchiseId'

// Type components
import FormControlTypeCheckbox from "@/common/form-control-type/FormControlTypeCheckbox"
import FormControlTypeColor from "@/common/form-control-type/FormControlTypeColor"
import FormControlTypeCodeEditor from "@/common/form-control-type/FormControlTypeCodeEditor"
import FormControlTypeCollectionFilters from "@/common/form-control-type/FormControlTypeCollectionFilters.vue"
import FormControlTypeCurrency from "@/common/form-control-type/FormControlTypeCurrency"
import FormControlTypeDate from "@/common/form-control-type/FormControlTypeDate"
import FormControlTypeDateDate from "@/common/form-control-type/FormControlTypeDateDate"
import FormControlTypeDatetimeLocal from "@/common/form-control-type/FormControlTypeDatetimeLocal"
import FormControlTypeEmail from "@/common/form-control-type/FormControlTypeEmail"
import FormControlTypeFile from "@/common/form-control-type/FormControlTypeFile"
import FormControlTypeImage from "@/common/form-control-type/FormControlTypeImage"
import FormControlTypeInternalLink from "@/common/form-control-type/FormControlTypeInternalLink"
import FormControlTypeJson from "@/common/form-control-type/FormControlTypeJson"
import FormControlTypeMonth from "@/common/form-control-type/FormControlTypeMonth"
import FormControlTypeNumber from "@/common/form-control-type/FormControlTypeNumber"
import FormControlTypePassword from "@/common/form-control-type/FormControlTypePassword"
import FormControlTypeRange from "@/common/form-control-type/FormControlTypeRange"
import FormControlTypeRelationshipOneToMany from "@/common/form-control-type/FormControlTypeRelationshipOneToMany"
import FormControlTypeRelationshipManyToMany from "@/common/form-control-type/FormControlTypeRelationshipManyToMany"
import FormControlTypeSelect from "@/common/form-control-type/FormControlTypeSelect"
import FormControlTypeSelectOther from "@/common/form-control-type/FormControlTypeSelectOther"
import FormControlTypeTable from "@/common/form-control-type/FormControlTypeTable"
import FormControlTypeTel from "@/common/form-control-type/FormControlTypeTel"
import FormControlTypeText from "@/common/form-control-type/FormControlTypeText"
import FormControlTypeTextarea from "@/common/form-control-type/FormControlTypeTextarea"
import FormControlTypeTexteditor from "@/common/form-control-type/FormControlTypeTexteditor"
import FormControlTypeTime from "@/common/form-control-type/FormControlTypeTime"
import FormControlTypeUrl from "@/common/form-control-type/FormControlTypeUrl"
import FormControlTypeWeek from "@/common/form-control-type/FormControlTypeWeek"
import {useAppStore} from "@/stores/appStore"

/**
 * COMPONENT REGISTRY
 * Replaces the $options.components lookup.
 * Every component used must be present in this object.
 */
const COMPONENTS = {
    FormControlCoursesDatesBookings,
    FormControlMRegistrantsRegistrantsCreditAmount,
    FormControlOdpCourseBookingsAmountPaid,
    FormControlOdpCourseBookingsCourseDateId,
    FormControlOdpCourseDatesPromotionId,
    FormControlOdpCourseDatesCourseId,
    FormControlOdpCourseDatesBookings,
    FormControlOdpCourseDatesEvent,
    FormControlOdpPartyBookingsResendEmail,
    FormControlCategoriesComponentMenu,
    FormControlActivityRowId,
    FormControlUsersAdministrator,
    FormControlUsersAllowCacheClearance,
    FormControlUsersRoles,
    FormControlUsersGroupsRoles,
    FormControlProductVariablesOptionId,
    FormControlProductTypesButtons,
    FormControlProductTypesFeatures,
    FormControlProductTypesShipping,
    FormControlProductOptionsButtons,
    FormControlProductOptionsProductTypeId,
    FormControlMProductsProductsTypeId,
    FormControlMProductsProductsCategoryId,
    FormControlMProductsProductsTypeFeatures,
    FormControlPagesComponentId,
    FormControlPagesDisplayInSitemap,
    FormControlPagesNavigation,
    FormControlPagesTemplateId,
    FormControlPagesType,
    FormControlPagesUrl,
    FormControlPagesUrlSlug,
    FormControlMRegistrantsRegistrantsLogin,
    FormControlMRegistrantsRegistrantsSessionLogs,
    FormControlMRegistrantsRegistrantsUsername,
    FormControlMBasketMerchantdetailsSettings,
    FormControlMBasketOrderitemsVariationId,
    FormControlMBasketOrdersCustomerEmailMessageId,
    FormControlMBasketOrdersInternalEmailMessageId,
    FormControlMBasketOrdersLogs,
    FormControlMBasketOrdersRequestAReview,
    FormControlMBasketOrdersQuantity,
    FormControlMBasketOrdersSessionLog,
    FormControlModulecomponentsDisplayName,
    FormControlModulecomponentsNameField,
    FormControlModulecomponentsTitleColumn,
    FormControlModulecomponentsThumbnailColumn,
    FormControlModulecomponentsTableName,
    FormControlModulecomponentsCategoryColumn,
    FormControlModulecomponentsCategoryComponent,
    FormControlAccountSettingsSecondaryDomains,
    FormControlAccountSettingsPrimaryDomain,
    FormControlAccountSettingsReorderScripts,
    FormControlContentPageUse,
    FormControlContentClass,
    FormControlContentTitle,
    FormControlContentContentType,
    FormControlContentContentTargetContentArea,
    FormControlMProductsProductsShipping,
    FormControlModulestructureBrowser,
    FormControlModulestructureCategoryComponentId,
    FormControlModulestructureColumnName,
    FormControlModulestructureDateFormat,
    FormControlModulestructureDefaultValue,
    FormControlModulestructureEndYear,
    FormControlModulestructureStartYear,
    FormControlModulestructureNumRows,
    FormControlModulestructureOptions,
    FormControlModulestructurePassword,
    FormControlModulestructureSelectType,
    FormControlModulestructureType,
    FormControlModulestructureTableTargetColumn,
    FormControlStockQuantity,
    FormControlUrlSlug,
    FormControlPortalSitesBuildName,
    FormControlEmailMessageAttachments,
    FormControlEmailMessageEvent,
    FormControlEmailMessageEventDate,
    FormControlEmailMessageHtmlBody,
    FormControlVariationsOption,
    FormControlTagsTagUsage,
    FormControlThemesCssGroups,
    FormControlTemplatesTemplatesComponentId,
    FormControlTemplatesTemplatesContentType,
    FormControlTemplatesTemplatesCssGroupName,
    FormControlTemplatesTemplatesFilterAttribute,
    FormControlTemplatesTemplatesLazyLoad,
    FormControlFilesFileName,
    FormControlFilesFileSize,
    FormControlFilesUsage,
    FormControlFranchiseId,
    FormControlTypeCheckbox,
    FormControlTypeColor,
    FormControlTypeCodeEditor,
    FormControlTypeCollectionFilters,
    FormControlTypeCurrency,
    FormControlTypeDate,
    FormControlTypeDateDate,
    FormControlTypeDatetimeLocal,
    FormControlTypeEmail,
    FormControlTypeFile,
    FormControlTypeImage,
    FormControlTypeInternalLink,
    FormControlTypeJson,
    FormControlTypeMonth,
    FormControlTypeNumber,
    FormControlTypePassword,
    FormControlTypeRange,
    FormControlTypeRelationshipOneToMany,
    FormControlTypeRelationshipManyToMany,
    FormControlTypeSelect,
    FormControlTypeSelectOther,
    FormControlTypeTable,
    FormControlTypeTel,
    FormControlTypeText,
    FormControlTypeTextarea,
    FormControlTypeTexteditor,
    FormControlTypeTime,
    FormControlTypeUrl,
    FormControlTypeWeek
}

const props = defineProps({
    formId: String,
    field: Object,
})

const route = useRoute()
const componentStore = useComponentsStore()
const itemDataStore = useItemDataStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// Use shallowRef to keep component definitions from being made reactive
const componentName = shallowRef(null)

// --- Computed ---

const currentData = computed(() => formStore.value?.currentData || {})
const component = computed(() => componentStore.items.find(o => o.tableName === route.params.component))
const tableName = computed(() => formStore.value?.tableName)

// --- Logic ---

const setType = () => {
    if (props.field.type === 'select') {
        let selectType = parseInt(props.field.selectType)
        if (props.field.otherInput || selectType === 1) {
            props.field.type = 'select-other'
        } else if (selectType === 2) {
            props.field.type = 'switch-selects'
        }
    }
}

const init = async () => {
    // 1. Logic for cleaning up fields
    let toBeDeleted = [
        {tableName: 'users', fieldNames: ['assume']},
        {tableName: 'modulestructure', fieldNames: ['customItem']},
        {tableName: 'm_products_products', fieldNames: ['variations']},
    ]

    let removed = false
    toBeDeleted.forEach((c) => {
        if (tableName.value === c.tableName && c.fieldNames.includes(props.field.columnName)) {
            props.field.hidden = true
            removed = true
        }
    })

    if (removed) return

    setType()

    // 2. Normalization
    props.field.name = props.field.name || props.field.columnName
    props.field.required = !!props.field.required
    props.field.disabled = !!props.field.disabled
    props.field.autofocus = !!props.field.autofocus
    props.field.readonly = !!props.field.readonly

    if (
        component.value?.allowFormAccess &&
        component.value?.navigationGroup === 'Forms' &&
        route.name === 'form'
    ) {
        props.field.readonly = true
    }

    if (props.field.readonly && useAppStore().betaMode) {
        props.field.readonly = false
    }

    // 3. Specially named components
    if (props.field.name === 'urlSlug' && tableName.value !== 'pages') {
        componentName.value = COMPONENTS.FormControlUrlSlug
        return
    } else if (props.field.name === 'stockQuantity') {
        componentName.value = COMPONENTS.FormControlStockQuantity
        return
    }

    // 4. Async content template lookup
    // todo The component this targets converts the rel field to a select menu.
    //      This could be replaced by a generic feature allowing rel fields to be presented as menus
    if (tableName.value === 'content' && props.field.name === 'componentId') {
        const template = await itemDataStore.get({
            tableName: 'templates_templates',
            id: currentData.value.contentType
        })
        if (template?.contentType === 'Categories') {
            componentName.value = COMPONENTS.FormControlCategoriesComponentMenu
            return
        }
    }

    // 5. Column-based custom lookup
    if (props.field.name) {
        let lookupTable = tableName.value ? tableName.value.toLowerCase().replace(/_/g, '-') : ''
        let lookupColumn = props.field.name

        if (lookupColumn === 'franchiseId' && lookupTable !== 'users') {
            componentName.value = COMPONENTS.FormControlFranchiseId
            return
        }

        if (tableName.value && tableName.value.indexOf('product_variations__') === 0) {
            lookupTable = 'variations'
            if (props.field.name.indexOf('option') === 0) lookupColumn = 'option'
        }

        let tag = 'form-control-' + lookupTable + '-' + lookupColumn
        let camel = camelCase(tag)
        let vueName = camel.charAt(0).toUpperCase() + camel.slice(1)

        if (COMPONENTS[vueName]) {
            componentName.value = COMPONENTS[vueName]
            return
        }
    }

    // 6. Type-based lookup (Fallback)
    if (['hidden', 'fieldset'].includes(props.field.type)) {
        props.field.hidden = true
        return
    }

    let typeName = 'form-control-type-' + props.field.type
    let typeCamel = camelCase(typeName)
    let typeVueName = typeCamel.charAt(0).toUpperCase() + typeCamel.slice(1)

    if (COMPONENTS[typeVueName]) {
        componentName.value = COMPONENTS[typeVueName]
    } else {
        console.warn('form-control component not found', {
            'field.name': props.field.name,
            vueComponentName: typeVueName,
        })
    }
}

onMounted(() => {
    init()
})
</script>

<template>
    <component
        :is="componentName"
        v-if="componentName"
        :current-data="currentData"
        :field="field"
        :form-id="formId"
        :value="currentData[field.name]"
    />
</template>